<template>
  <Teleport to="body">
    <VcLoaderOverlay v-if="reverting" fixed-spinner data-test-id="mobile-back-to-operator-loader">
      {{ $t("shared.layout.header.top_header.switching_back") }}
    </VcLoaderOverlay>
  </Teleport>

  <section class="grow divide-y divide-additional-50 divide-opacity-20 overflow-y-auto">
    <ul class="flex flex-col gap-y-2 px-9 py-6">
      <li>
        <MobileMenuLink :link="menuItem" class="py-1 text-2xl font-bold" @close="$emit('close')">
          {{ menuItem.title }}
        </MobileMenuLink>
      </li>

      <li v-for="item in mobileMainMenuItems" :key="item.title">
        <ExtensionPoint
          category="mobileMenu"
          :name="item.id"
          :item="item"
          @close="$emit('close')"
          @select-item="$emit('selectItem', item)"
        >
          <template #default="{ extensionProps }">
            <LinkDefault
              :item="item"
              :count="toValue(extensionProps?.count)"
              @close="$emit('close')"
              @select-item="$emit('selectItem', item)"
            />
          </template>
        </ExtensionPoint>
      </li>
    </ul>

    <div class="flex flex-col gap-y-2 px-9 py-6">
      <template v-if="isAuthenticated">
        <!-- Account -->
        <div class="my-2 flex flex-row gap-4 text-xl">
          <div
            class="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-accent-300"
          >
            <VcImage v-if="user.photoUrl" :src="user.photoUrl" :alt="user.contact?.fullName" class="size-12" lazy />

            <VcIcon v-else name="user" />
          </div>

          <div
            class="line-clamp-3 flex flex-wrap items-center gap-x-1 text-[--mobile-menu-text-color] [word-break:break-word]"
          >
            <template v-if="operator">
              <span class="font-bold">
                {{ operator.contact?.fullName || operator.userName }}
              </span>

              {{ $t("shared.layout.header.top_header.logged_in_as") }}
            </template>

            <span class="font-bold">
              {{ user.contact?.fullName || user.userName }}
            </span>
          </div>
        </div>

        <div class="mb-4 flex justify-between gap-2">
          <button
            v-if="operator"
            type="button"
            class="flex items-center gap-1 font-bold text-[--mobile-menu-navigation-color]"
            data-test-id="mobile-back-to-operator-button"
            @click="onBackToOperator"
          >
            <VcIcon name="arrow-left" />

            <span>{{ backToOperatorLabel }}</span>
          </button>

          <button
            type="button"
            class="group flex items-center gap-1 font-bold text-[--mobile-menu-link-color]"
            data-test-id="mobile-account-menu-logout-row"
            @click="signMeOut"
          >
            <VcIcon name="logout" />

            <span>{{ $t("shared.layout.header.link_logout") }}</span>
          </button>
        </div>

        <!-- Account sections -->
        <ul class="flex flex-col gap-y-2">
          <!-- Registered sections (e.g. Sales Rep hub) always lead, in registration order. Mobile does
               NOT honor `priority` (desktop does): the built-ins below are hardcoded blocks, so there's
               no list to interleave into. Priority-aware mobile is deferred to the mobile-menu redesign.
               See AccountNavigationSectionType.priority. -->
          <li v-for="section in mobileRegisteredAccountSections" :key="section.id">
            <MobileMenuLink :link="section" class="py-1 text-2xl font-bold" @select="$emit('selectItem', section)">
              {{ section.title }}
            </MobileMenuLink>
          </li>

          <!-- Purchasing -->
          <li>
            <MobileMenuLink
              v-if="mobilePurchasingMenuItem"
              :link="mobilePurchasingMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobilePurchasingMenuItem!)"
            >
              {{ mobilePurchasingMenuItem.title }}
            </MobileMenuLink>
          </li>

          <!-- Marketing -->
          <li>
            <MobileMenuLink
              v-if="mobileMarketingMenuItem && mobileMarketingMenuItem.children?.length"
              :link="mobileMarketingMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobileMarketingMenuItem!)"
            >
              {{ mobileMarketingMenuItem.title }}
            </MobileMenuLink>
          </li>

          <!-- Corporate -->
          <li>
            <MobileMenuLink
              v-if="mobileCorporateMenuItem && isCorporateMember"
              :link="mobileCorporateMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobileCorporateMenuItem!)"
            >
              {{ mobileCorporateMenuItem.title }}
            </MobileMenuLink>
          </li>

          <!-- User -->
          <li>
            <MobileMenuLink
              v-if="mobileUserMenuItem"
              :link="mobileUserMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobileUserMenuItem!)"
            >
              {{ mobileUserMenuItem.title }}
            </MobileMenuLink>
          </li>
        </ul>
      </template>

      <!-- Unauthorized links -->
      <ul v-else class="mb-1">
        <li>
          <MobileMenuLink
            v-for="item in unauthorizedMenuItems"
            :key="item.title"
            :link="item"
            class="py-1.5 text-2xl font-bold"
            @close="$emit('close')"
          >
            {{ item.title }}
          </MobileMenuLink>
        </li>
      </ul>

      <!-- Settings link -->
      <MobileMenuLink
        v-if="supportedCurrencies.length > 1"
        :link="settingsMenuItem"
        class="py-1 text-2xl font-bold"
        @select="$emit('selectItem', settingsMenuItem)"
      >
        {{ $t("shared.layout.header.mobile.settings") }}
      </MobileMenuLink>
    </div>
  </section>
</template>

<script setup lang="ts">
import { toValue } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency, useNavigations } from "@/core/composables";
import { ROUTES } from "@/router/routes/constants";
import { useImpersonate, useSignMeOut, useUser } from "@/shared/account";
import type { ExtendedMenuLinkType } from "@/core/types";
import LinkDefault from "@/shared/layout/components/header/_internal/mobile-menu/link-components/link-default.vue";
import MobileMenuLink from "@/shared/layout/components/header/_internal/mobile-menu/mobile-menu-link.vue";

interface IProps {
  menuItem: ExtendedMenuLinkType;
}

interface IEmits {
  (event: "close"): void;
  (event: "selectItem", item: ExtendedMenuLinkType): void;
}

defineEmits<IEmits>();
defineProps<IProps>();

const { signMeOut } = useSignMeOut();
const { user, operator, isAuthenticated, isCorporateMember } = useUser();
const { reverting, backToOperatorLabel, backToOperator: onBackToOperator } = useImpersonate();
const {
  mobileMainMenuItems,
  mobilePurchasingMenuItem,
  mobileMarketingMenuItem,
  mobileUserMenuItem,
  mobileCorporateMenuItem,
  mobileRegisteredAccountSections,
} = useNavigations();
const { t } = useI18n();
const { supportedCurrencies } = useCurrency();

const unauthorizedMenuItems: ExtendedMenuLinkType[] = [
  { route: { name: ROUTES.SIGN_IN.NAME }, title: t("shared.layout.header.link_sign_in") },
  { route: { name: "SignUp" }, title: t("shared.layout.header.link_register_now") },
];

const settingsMenuItem: ExtendedMenuLinkType = {
  id: "settings",
  icon: "cog",
  children: [{}],
};
</script>
